import { reactive } from 'vue'

const DEFAULTS = {
  showBPMInTrackView: true,
  showLUFSInTrackView: true,
  showKeyInTrackView: true,
  liveLoudnessMetering: false,
  persistEffectEachTrack: false,
  locale: null,
  searchShortcut: { key: 'tab', ctrlKey: false, metaKey: false, altKey: false, shiftKey: false }
}

const SETTING_KEYS = Object.keys(DEFAULTS)
const CLOUD_SAVE_DEBOUNCE_MS = 600
const CLOUD_SAVE_RETRY_INITIAL_MS = 2000
const CLOUD_SAVE_RETRY_MAX_MS = 60000

let cloudClient = null
let cloudSaveTimer = null
let lastSyncedSnapshot = null
let savePromise = null
let cloudSaveRetryCount = 0
let cloudSavePaused = false
const subscribers = new Set()

function parseSavedSettings() {
  try {
    const saved = localStorage.getItem('settings')
    return saved ? JSON.parse(saved) : {}
  } catch (error) {
    console.warn('Failed to parse saved settings:', error)
    return {}
  }
}

function normalizeShortcut(shortcut) {
  if (!shortcut || typeof shortcut !== 'object' || Array.isArray(shortcut)) {
    return { ...DEFAULTS.searchShortcut }
  }

  return {
    key: typeof shortcut.key === 'string' && shortcut.key ? shortcut.key.toLowerCase() : DEFAULTS.searchShortcut.key,
    ctrlKey: Boolean(shortcut.ctrlKey),
    metaKey: Boolean(shortcut.metaKey),
    altKey: Boolean(shortcut.altKey),
    shiftKey: Boolean(shortcut.shiftKey)
  }
}

function normalizeSettings(settings = {}) {
  const normalized = {}

  for (const key of SETTING_KEYS) {
    if (settings[key] === undefined) continue

    if (key === 'searchShortcut') {
      normalized.searchShortcut = normalizeShortcut(settings.searchShortcut)
      continue
    }

    if (key === 'locale') {
      normalized.locale = typeof settings.locale === 'string' ? settings.locale : null
      continue
    }

    normalized[key] = Boolean(settings[key])
  }

  return normalized
}

function fullSettingsSnapshot(settings = state) {
  return {
    showBPMInTrackView: Boolean(settings.showBPMInTrackView),
    showLUFSInTrackView: Boolean(settings.showLUFSInTrackView),
    showKeyInTrackView: Boolean(settings.showKeyInTrackView),
    liveLoudnessMetering: Boolean(settings.liveLoudnessMetering),
    persistEffectEachTrack: Boolean(settings.persistEffectEachTrack),
    locale: typeof settings.locale === 'string' ? settings.locale : null,
    searchShortcut: normalizeShortcut(settings.searchShortcut)
  }
}

function snapshotString(settings) {
  return JSON.stringify(fullSettingsSnapshot(settings))
}

function persistLocal() {
  localStorage.setItem('settings', JSON.stringify(fullSettingsSnapshot()))
}

function resetCloudSaveRetry() {
  cloudSaveRetryCount = 0
  cloudSavePaused = false
}

function isRetryableCloudSaveError(error) {
  const status = Number(error?.status)
  if (!Number.isFinite(status)) return true
  return status === 408 || status === 429 || status >= 500
}

function getCloudSaveRetryDelay() {
  const retryIndex = Math.max(0, cloudSaveRetryCount - 1)
  return Math.min(CLOUD_SAVE_RETRY_MAX_MS, CLOUD_SAVE_RETRY_INITIAL_MS * (2 ** retryIndex))
}

function notifySubscribers(key, value, previousValue, source) {
  subscribers.forEach((callback) => {
    try {
      callback(key, value, previousValue, source)
    } catch (error) {
      console.warn('Settings subscriber failed:', error)
    }
  })
}

const saved = parseSavedSettings()

const state = reactive(
  {
    ...DEFAULTS,
    ...saved,
    searchShortcut: normalizeShortcut(saved.searchShortcut)
  }
)

function applySettings(nextSettings, { source = 'local', sync = true } = {}) {
  const normalized = normalizeSettings(nextSettings)

  for (const [key, value] of Object.entries(normalized)) {
    const previousValue = state[key]
    const previousSerialized = JSON.stringify(previousValue)
    const nextSerialized = JSON.stringify(value)
    if (previousSerialized === nextSerialized) continue

    state[key] = value
    notifySubscribers(key, value, previousValue, source)
  }

  persistLocal()
  if (sync) scheduleCloudSave()
}

function set(key, value) {
  if (!SETTING_KEYS.includes(key)) return
  applySettings({ [key]: value })
}

function get(key) {
  return state[key]
}

function hydrateFromCloud(cloudSettings) {
  applySettings(cloudSettings || {}, { source: 'cloud', sync: false })
  lastSyncedSnapshot = snapshotString()
  resetCloudSaveRetry()
}

function configureCloudSync(client) {
  if (cloudClient !== client) {
    lastSyncedSnapshot = null
    resetCloudSaveRetry()
  }
  cloudClient = client || null
  if (!cloudClient && cloudSaveTimer) {
    clearTimeout(cloudSaveTimer)
    cloudSaveTimer = null
    resetCloudSaveRetry()
  }
}

function syncWithCloud(client, cloudSettings) {
  configureCloudSync(client)
  const normalizedCloudSettings = normalizeSettings(cloudSettings || {})
  if (Object.keys(normalizedCloudSettings).length > 0) {
    hydrateFromCloud(normalizedCloudSettings)
    return
  }

  scheduleCloudSave(0)
}

function scheduleCloudSave(delay = CLOUD_SAVE_DEBOUNCE_MS, { resetRetry = true } = {}) {
  if (!cloudClient?.updateSettings) return
  if (resetRetry) resetCloudSaveRetry()
  if (cloudSavePaused) return

  if (cloudSaveTimer) {
    clearTimeout(cloudSaveTimer)
  }

  cloudSaveTimer = setTimeout(() => {
    cloudSaveTimer = null
    flushCloudSettings().catch((error) => {
      console.warn('Failed to sync settings:', error)
    })
  }, delay)
}

async function flushCloudSettings() {
  if (!cloudClient?.updateSettings) return false

  const payload = fullSettingsSnapshot()
  const nextSnapshot = JSON.stringify(payload)
  if (nextSnapshot === lastSyncedSnapshot) return true

  if (savePromise) return savePromise

  const requestClient = cloudClient
  const currentSavePromise = Promise.resolve().then(async () => {
    let shouldScheduleRetry = false

    try {
      const response = await requestClient.updateSettings(payload)
      const savedSettings = normalizeSettings(response?.settings || payload)
      applySettings(savedSettings, { source: 'cloud', sync: false })
      lastSyncedSnapshot = snapshotString()
      resetCloudSaveRetry()
      return true
    } catch (error) {
      if (cloudClient === requestClient) {
        shouldScheduleRetry = isRetryableCloudSaveError(error)
        if (shouldScheduleRetry) {
          cloudSaveRetryCount += 1
        } else {
          cloudSavePaused = true
        }
      }

      throw error
    } finally {
      if (savePromise === currentSavePromise) {
        savePromise = null
      }

      if (cloudClient === requestClient && shouldScheduleRetry && snapshotString() !== lastSyncedSnapshot) {
        scheduleCloudSave(getCloudSaveRetryDelay(), { resetRetry: false })
      }
    }
  })()

  savePromise = currentSavePromise

  return savePromise
}

function subscribe(callback) {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

export const settings = {
  state,
  get,
  set,
  hydrateFromCloud,
  configureCloudSync,
  syncWithCloud,
  flushCloudSettings,
  subscribe
}
